import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Printer, FileSpreadsheet, FileDown, Download } from 'lucide-react';
import { format, startOfDay, startOfMonth, subDays } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useBrandingContext } from '@/contexts/BrandingContext';
import { useSites } from '@/hooks/useSites';
import { downloadCsv, downloadExcel, printReport, type ReportPayload } from '@/utils/reportExport';

export interface ReportColumn<T> {
  key: string;
  label: string;
  value: (row: T) => string | number;
  /** Set false to leave the column unchecked by default. */
  defaultOn?: boolean;
}

interface ReportButtonProps<T> {
  /** Report title used in the header and filenames. */
  title: string;
  rows: T[];
  columns: ReportColumn<T>[];
  /** Return the row's date to enable date-range filtering. */
  getDate?: (row: T) => string | Date | null | undefined;
  /** Return the row's site id to enable facility scoping. */
  getSiteId?: (row: T) => string | null | undefined;
  /** Key metrics rendered above the table, computed from the filtered rows. */
  summary?: (rows: T[]) => Record<string, string | number>;
  label?: string;
  size?: 'sm' | 'default';
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
}

type RangeKey = 'all' | 'today' | '7' | '30' | '90' | 'month' | 'custom';

const RANGE_LABELS: Record<Exclude<RangeKey, 'custom'>, string> = {
  all: 'All time',
  today: 'Today',
  '7': 'Last 7 days',
  '30': 'Last 30 days',
  '90': 'Last 90 days',
  month: 'This month',
};

export function ReportButton<T>({
  title,
  rows,
  columns,
  getDate,
  getSiteId,
  summary,
  label = 'Print / Export',
  size = 'sm',
  variant = 'outline',
}: ReportButtonProps<T>) {
  const { user } = useAuth();
  const { organizationName, logoUrl } = useBrandingContext();
  const { sites = [] } = useSites();

  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<RangeKey>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [siteId, setSiteId] = useState('all');
  const [selected, setSelected] = useState<string[]>(
    columns.filter((c) => c.defaultOn !== false).map((c) => c.key)
  );
  const [busy, setBusy] = useState(false);

  const bounds = useMemo(() => {
    const now = new Date();
    switch (range) {
      case 'today': return { from: startOfDay(now), to: null as Date | null };
      case '7': return { from: startOfDay(subDays(now, 7)), to: null };
      case '30': return { from: startOfDay(subDays(now, 30)), to: null };
      case '90': return { from: startOfDay(subDays(now, 90)), to: null };
      case 'month': return { from: startOfMonth(now), to: null };
      case 'custom':
        return {
          from: customFrom ? new Date(`${customFrom}T00:00:00`) : null,
          to: customTo ? new Date(`${customTo}T23:59:59`) : null,
        };
      default: return { from: null, to: null };
    }
  }, [range, customFrom, customTo]);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (getSiteId && siteId !== 'all' && getSiteId(row) !== siteId) return false;
      if (getDate && (bounds.from || bounds.to)) {
        const raw = getDate(row);
        if (!raw) return false;
        const d = raw instanceof Date ? raw : new Date(raw);
        if (Number.isNaN(d.getTime())) return false;
        if (bounds.from && d < bounds.from) return false;
        if (bounds.to && d > bounds.to) return false;
      }
      return true;
    });
  }, [rows, siteId, bounds, getDate, getSiteId]);

  const activeColumns = columns.filter((c) => selected.includes(c.key));

  const dateRangeLabel =
    !getDate
      ? 'Current view'
      : range === 'custom'
        ? `${customFrom ? format(new Date(`${customFrom}T00:00:00`), 'MMM d, yyyy') : 'Beginning'} – ${customTo ? format(new Date(`${customTo}T00:00:00`), 'MMM d, yyyy') : 'Today'}`
        : RANGE_LABELS[range];

  const buildPayload = (): ReportPayload | null => {
    if (activeColumns.length === 0) {
      toast.error('Select at least one column');
      return null;
    }
    return {
      title,
      organizationName,
      logoUrl,
      facilityScope: siteId === 'all' ? 'All facilities' : sites.find((s) => s.id === siteId)?.name || 'Facility',
      dateRangeLabel,
      generatedBy: (user as any)?.name || (user as any)?.full_name || (user as any)?.email || 'Unknown user',
      summary: summary ? summary(filtered) : undefined,
      headers: activeColumns.map((c) => c.label),
      rows: filtered.map((row) => activeColumns.map((c) => {
        try { return c.value(row) ?? ''; } catch { return ''; }
      })),
    };
  };

  const run = async (mode: 'print' | 'csv' | 'excel') => {
    const payload = buildPayload();
    if (!payload) return;
    setBusy(true);
    try {
      if (mode === 'print') {
        if (!printReport(payload)) {
          toast.error('Pop-up blocked — allow pop-ups to print this report');
          return;
        }
      } else if (mode === 'csv') {
        downloadCsv(payload);
      } else {
        await downloadExcel(payload);
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.message || 'Could not generate the report');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className="gap-2">
          <Download className="h-4 w-4" />
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Choose the scope and columns, then print or export.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {getDate && (
            <div className="space-y-2">
              <Label>Date range</Label>
              <Select value={range} onValueChange={(v: RangeKey) => setRange(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="month">This month</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
              {range === 'custom' && (
                <div className="grid grid-cols-2 gap-2">
                  <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                  <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                </div>
              )}
            </div>
          )}

          {getSiteId && (
            <div className="space-y-2">
              <Label>Facility</Label>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All facilities</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Columns</Label>
              <button
                type="button"
                className="text-xs text-muted-foreground underline"
                onClick={() =>
                  setSelected(selected.length === columns.length ? [] : columns.map((c) => c.key))
                }
              >
                {selected.length === columns.length ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto rounded-md border p-3">
              {columns.map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={selected.includes(c.key)} onCheckedChange={() => toggle(c.key)} />
                  <span className="truncate">{c.label}</span>
                </label>
              ))}
            </div>
          </div>

          <Separator />

          <p className="text-xs text-muted-foreground">
            {filtered.length} record{filtered.length === 1 ? '' : 's'} · {dateRangeLabel}
            {getSiteId && ` · ${siteId === 'all' ? 'All facilities' : sites.find((s) => s.id === siteId)?.name || ''}`}
          </p>

          <div className="grid grid-cols-3 gap-2">
            <Button onClick={() => run('print')} disabled={busy} className="gap-2">
              <Printer className="h-4 w-4" /> Print / PDF
            </Button>
            <Button variant="outline" onClick={() => run('csv')} disabled={busy} className="gap-2">
              <FileDown className="h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" onClick={() => run('excel')} disabled={busy} className="gap-2">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ReportButton;
